/**
 * V2 Conformance — rule engine
 *
 * Interprets the declarations in `legacyPathMap.ts`. ONE engine drives both the
 * validator (detect-only, never mutates) and the normalizer (mutates a clone),
 * selected by the `apply` flag — so detection and repair can never drift.
 *
 * Migrate-then-strip discipline (AC-009/AC-012): a legacy value is copied to its
 * canonical home FIRST, and the legacy key is removed only after; when both
 * already exist, canonical wins and the legacy copy is dropped (reported).
 *
 * @module screen-utils/v2-conformance
 */

import {
  CONFORMANCE_RULES,
  type ConformanceRule,
  type LeafMoveRule,
  type ObjectMergeRule,
  type EnumRenameRule,
  type KeyRenameRule,
  type SiblingMoveRule,
  type DropFieldRule,
} from './legacyPathMap';
import {
  resolveHosts,
  getLeaf,
  setLeaf,
  deleteLeaf,
  isPlainObject,
  type PlainObject,
} from './pathPattern';

export interface ConformanceChange {
  ruleId: string;
  concept: string;
  type: 'leafMove' | 'objectMerge' | 'enumRename' | 'keyRename' | 'siblingMove' | 'wrap' | 'dropField';
  /** Legacy locus that was found (key, path, or `key=value`). */
  legacy: string;
  /** Canonical locus it maps to. */
  canonical: string;
  /** Optional annotation, e.g. a kept-canonical conflict. */
  note?: string;
}

/**
 * Run the rule set over a StepContent-shaped object.
 * @param apply when false, no mutation occurs (validator); when true, the object
 *   is repaired in place (caller passes a clone).
 * @returns every legacy occurrence found (== what was/would-be repaired).
 */
export function runConformance(root: unknown, apply: boolean, onlyRuleIds?: string[]): ConformanceChange[] {
  const changes: ConformanceChange[] = [];
  if (!isPlainObject(root)) return changes;

  // Optional rule filter — a targeted repair (e.g. only `label.insets`) applies just
  // those rules, leaving lossy/unrelated drift (e.g. the `copy.subtitle2` drop) untouched.
  const rules = onlyRuleIds && onlyRuleIds.length
    ? CONFORMANCE_RULES.filter((r) => onlyRuleIds.includes(r.id))
    : CONFORMANCE_RULES;

  // PATH rules first, in declared order (objectMerge before its leafMoves;
  // dropField last so a value can be migrated before any sibling is dropped).
  for (const rule of rules) {
    if (rule.kind === 'objectMerge') applyObjectMerge(root, rule, apply, changes);
    else if (rule.kind === 'leafMove') applyLeafMove(root, rule, apply, changes);
  }
  for (const rule of rules) {
    if (rule.kind === 'dropField') applyDropField(root, rule, apply, changes);
  }

  // SHAPE rules in a single tree walk.
  const shapeRules = rules.filter(
    (r): r is EnumRenameRule | KeyRenameRule | SiblingMoveRule =>
      r.kind === 'enumRename' || r.kind === 'keyRename' || r.kind === 'siblingMove',
  );
  if (shapeRules.length) walkShape(root, undefined, shapeRules, apply, changes);

  return changes;
}

// ── PATH rule handlers ──────────────────────────────────────────────────────

function applyObjectMerge(
  root: PlainObject,
  rule: ObjectMergeRule,
  apply: boolean,
  changes: ConformanceChange[],
): void {
  for (const host of resolveHosts(root, rule.base)) {
    // Raw-number canonical (e.g. progressIndicator: 50) → wrap as the renderer does.
    if (typeof host[rule.toKey] === 'number') {
      if (apply) host[rule.toKey] = { value: host[rule.toKey], kind: 'linearBar' };
      changes.push({
        ruleId: rule.id, concept: rule.concept, type: 'wrap',
        legacy: `${rule.toKey}=<number>`, canonical: `${rule.toKey}.value`,
      });
    }

    for (const fromKey of rule.fromKeys) {
      const src = host[fromKey];
      if (src === undefined || !isPlainObject(src)) continue;
      if (apply) {
        if (!isPlainObject(host[rule.toKey])) host[rule.toKey] = {};
        const dest = host[rule.toKey] as PlainObject;
        for (const k of Object.keys(src)) if (!(k in dest)) dest[k] = src[k]; // canonical wins
        delete host[fromKey];
      }
      changes.push({
        ruleId: rule.id, concept: rule.concept, type: 'objectMerge',
        legacy: fromKey, canonical: rule.toKey,
      });
    }
  }
}

function applyLeafMove(
  root: PlainObject,
  rule: LeafMoveRule,
  apply: boolean,
  changes: ConformanceChange[],
): void {
  for (const host of resolveHosts(root, rule.base)) {
    const presentLegacy = rule.legacyLeaves.filter((ll) => getLeaf(host, ll).exists);
    if (presentLegacy.length === 0) continue;

    const canon = getLeaf(host, rule.canonicalLeaf);
    const canonHasValue = canon.exists && canon.value !== undefined && canon.value !== null;

    if (apply) {
      if (!canonHasValue) {
        setLeaf(host, rule.canonicalLeaf, getLeaf(host, presentLegacy[0]).value);
      }
      for (const ll of presentLegacy) deleteLeaf(host, ll);
    }
    changes.push({
      ruleId: rule.id, concept: rule.concept, type: 'leafMove',
      legacy: presentLegacy.join('|'), canonical: rule.canonicalLeaf,
      note: canonHasValue ? 'conflict: canonical kept' : undefined,
    });
  }
}

function applyDropField(
  root: PlainObject,
  rule: DropFieldRule,
  apply: boolean,
  changes: ConformanceChange[],
): void {
  for (const host of resolveHosts(root, rule.base)) {
    for (const field of rule.fields) {
      const ref = getLeaf(host, field);
      if (!ref.exists) continue;
      if (apply) deleteLeaf(host, field);
      changes.push({
        ruleId: rule.id, concept: rule.concept, type: 'dropField',
        legacy: field, canonical: '<dropped>',
        note: rule.lossy ? 'orphan: no V2 home — content lost' : 'orphan dropped',
      });
    }
  }
}

// ── SHAPE rule walk ─────────────────────────────────────────────────────────

function renameKey(
  node: PlainObject,
  rule: KeyRenameRule,
  apply: boolean,
  changes: ConformanceChange[],
): void {
  const hasCanonical = rule.canonicalKey in node && node[rule.canonicalKey] !== undefined;
  if (apply) {
    const legacyVal = node[rule.legacyKey];
    if (!hasCanonical) {
      node[rule.canonicalKey] = legacyVal;
    } else if (isPlainObject(node[rule.canonicalKey]) && isPlainObject(legacyVal)) {
      const dest = node[rule.canonicalKey] as PlainObject;
      for (const k of Object.keys(legacyVal)) if (!(k in dest)) dest[k] = (legacyVal as PlainObject)[k];
    }
    delete node[rule.legacyKey];
  }
  changes.push({
    ruleId: rule.id, concept: rule.concept, type: 'keyRename',
    legacy: rule.legacyKey, canonical: rule.canonicalKey,
    note: hasCanonical ? 'conflict: canonical kept' : undefined,
  });
}

/** EdgeInsets counts as "empty" when every present value is 0/null/undefined. */
function isEmptyInsets(v: unknown): boolean {
  if (!isPlainObject(v)) return !v;
  return Object.values(v).every((x) => x === 0 || x === null || x === undefined);
}

/** Move `node[fromContainer][childKey]` → `node[toContainer][childKey]` on a shape-matched node. */
function applySiblingMove(
  node: PlainObject,
  rule: SiblingMoveRule,
  apply: boolean,
  changes: ConformanceChange[],
  parentKey?: string,
): void {
  // Shape guard: skip nodes reached via an excluded parent key (e.g. ProductBadge under `badge`).
  if (rule.notUnderParentKey !== undefined && parentKey === rule.notUnderParentKey) return;
  if (!rule.identifiedBy.every((k) => k in node)) return;
  const from = node[rule.fromContainer];
  if (!isPlainObject(from) || !(rule.childKey in from)) return;

  const dest = isPlainObject(node[rule.toContainer]) ? (node[rule.toContainer] as PlainObject) : undefined;
  const destChild = dest ? dest[rule.childKey] : undefined;
  const destHasReal =
    destChild !== undefined && destChild !== null &&
    !(rule.emptyDestMeansAbsent && isEmptyInsets(destChild));

  if (apply) {
    if (!destHasReal) {
      if (!isPlainObject(node[rule.toContainer])) node[rule.toContainer] = {};
      (node[rule.toContainer] as PlainObject)[rule.childKey] = (from as PlainObject)[rule.childKey];
    }
    delete (from as PlainObject)[rule.childKey];
  }
  changes.push({
    ruleId: rule.id, concept: rule.concept, type: 'siblingMove',
    legacy: `${rule.fromContainer}.${rule.childKey}`,
    canonical: `${rule.toContainer}.${rule.childKey}`,
    note: destHasReal ? 'conflict: canonical kept' : undefined,
  });
}

function walkShape(
  node: unknown,
  parentKey: string | undefined,
  rules: (EnumRenameRule | KeyRenameRule | SiblingMoveRule)[],
  apply: boolean,
  changes: ConformanceChange[],
): void {
  if (Array.isArray(node)) {
    for (const el of node) walkShape(el, parentKey, rules, apply, changes);
    return;
  }
  if (!isPlainObject(node)) return;

  for (const rule of rules) {
    if (rule.kind === 'keyRename') {
      if (rule.anyElement) {
        if (rule.legacyKey in node) renameKey(node, rule, apply, changes);
      } else if (rule.underParentKey !== undefined && parentKey === rule.underParentKey) {
        // `node` is the container map (e.g. translations); rename on its element values.
        for (const childKey of Object.keys(node)) {
          const child = node[childKey];
          if (isPlainObject(child) && rule.legacyKey in child) renameKey(child, rule, apply, changes);
        }
      }
    } else if (rule.kind === 'siblingMove') {
      applySiblingMove(node, rule, apply, changes, parentKey);
    } else {
      // enumRename
      const identified = rule.identifiedBy.every((k) => k in node);
      const cur = node[rule.enumKey];
      if (identified && typeof cur === 'string' && cur in rule.valueMap) {
        if (apply) node[rule.enumKey] = rule.valueMap[cur];
        changes.push({
          ruleId: rule.id, concept: rule.concept, type: 'enumRename',
          legacy: `${rule.enumKey}=${cur}`, canonical: `${rule.enumKey}=${rule.valueMap[cur]}`,
        });
      }
    }
  }

  for (const key of Object.keys(node)) {
    walkShape(node[key], key, rules, apply, changes);
  }
}

/** Distinct rule ids that matched — handy for compact audit summaries. */
export function summarizeChanges(changes: ConformanceChange[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const c of changes) out[c.ruleId] = (out[c.ruleId] ?? 0) + 1;
  return out;
}
